/**
 * Presenting KV-token-seconds to humans.
 *
 * The unit is correct and unfamiliar, which is a bad combination for a billing
 * page. Every number the portal shows in this unit is shown *with* a plain
 * reading of it, or every support ticket will be about what it means.
 *
 * See `docs/software.md`, "Billing follows admission": charging per output
 * token misprices this workload by roughly 20x for long-context sessions,
 * which is why the meter reads in KV-token-seconds at all.
 */

/** A typical chat context, used as the yardstick in plain-language readings. */
const TYPICAL_CONTEXT_TOKENS = 25_000;

export function formatInteger(value: number): string {
  return new Intl.NumberFormat().format(Math.round(value));
}

/** `31000` → `31.0k`, for axis labels and dense tables. */
export function formatCompact(value: number): string {
  return new Intl.NumberFormat(undefined, {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(value);
}

/**
 * Turn KV-token-seconds into something a person can picture.
 *
 * The arithmetic: holding a 25k-token context for one second costs 25,000
 * KV-token-seconds, so dividing by that and by 3600 gives hours of a typical
 * chat session.
 */
export function explainKvTokenSeconds(kvTokenSeconds: number): string {
  if (kvTokenSeconds <= 0) {
    return 'nothing yet';
  }

  const seconds = kvTokenSeconds / TYPICAL_CONTEXT_TOKENS;
  if (seconds < 60) {
    return `about ${Math.max(1, Math.round(seconds))}s of a 25k-context chat`;
  }
  const minutes = seconds / 60;
  if (minutes < 60) {
    return `about ${Math.round(minutes)} min of a 25k-context chat`;
  }
  const hours = minutes / 60;
  return `about ${hours < 10 ? hours.toFixed(1) : Math.round(hours)} hours of a 25k-context chat`;
}

export function formatTimestamp(value: string | null): string {
  if (!value) {
    return 'never';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return 'unknown';
  }
  return date.toLocaleString();
}

/**
 * The share of prompt tokens that were served from the prefix cache.
 *
 * Derived rather than fetched: `cached_input_tokens` is a subset of
 * `input_tokens`, never a sibling of it, so the two must be divided and never
 * summed. It earns a place on the overview because it is the one number a user
 * can act on — restructuring a prompt so its stable prefix comes first moves
 * this, and a cache hit skips prefill and is billed at its own lower rate.
 */
export function cacheHitRate(input: number, cached: number): number | null {
  if (input <= 0) {
    return null;
  }
  return cached / input;
}

export function formatPercent(fraction: number): string {
  return new Intl.NumberFormat(undefined, {
    style: 'percent',
    maximumFractionDigits: 1,
  }).format(fraction);
}

// ---------------------------------------------------------------------------
// Credits
// ---------------------------------------------------------------------------

/**
 * Presenting credits to humans.
 *
 * A credit is 1,000 weighted tokens — see `docs/pricing_design.md`. The API
 * carries milli-credits, because the gate meters per request and a short turn
 * is a fraction of a credit; nothing outside this module should have to know
 * that.
 *
 * The same rule as KV-token-seconds applies, for the same reason: a number in
 * an unfamiliar unit on a billing page becomes a support ticket unless it is
 * shown next to a plain reading of itself.
 */

/** Milli-credits per credit. The API's unit, converted once, here. */
const MILLI_PER_CREDIT = 1_000;

/**
 * The rule of thumb from `docs/pricing_design.md` §1: one agent turn is about
 * ten credits.
 *
 * A rounder figure than the 9.48 the typical profile actually works out to,
 * deliberately — this is used for "about N turns", and quoting two decimal
 * places of precision on an estimate invites someone to hold us to it.
 */
const CREDITS_PER_AGENT_TURN = 10;

export function creditsOf(milli: number): number {
  return milli / MILLI_PER_CREDIT;
}

/**
 * Credits, at a precision that matches how big the number is.
 *
 * Two decimal places under ten credits, because that is where a single turn
 * lives and rounding it to "0" would make a working meter look broken. Whole
 * credits above that, because nobody reads the hundredths of a 25,000-credit
 * allowance.
 */
export function formatCredits(milli: number): string {
  const credits = creditsOf(milli);
  if (credits > 0 && credits < 10) {
    return new Intl.NumberFormat(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(credits);
  }
  return formatInteger(credits);
}

/** Turn a credit figure into something a person can picture. */
export function explainCredits(milli: number): string {
  const credits = creditsOf(milli);
  if (credits <= 0) {
    return 'nothing yet';
  }
  const turns = credits / CREDITS_PER_AGENT_TURN;
  if (turns < 1) {
    return 'under one agent turn';
  }
  if (turns < 10) {
    return `about ${turns.toFixed(1)} agent turns`;
  }
  return `about ${formatInteger(turns)} agent turns`;
}

/** How much of a window's allowance is gone, 0–1. `null` when uncapped. */
export function windowFraction(used: number, cap: number | null): number | null {
  if (cap === null || cap <= 0) {
    return null;
  }
  // Clamped: a window can legitimately end up over its cap — the gate admits
  // on an estimate and reconciles afterwards — and a bar rendered past 100%
  // overflows its track.
  return Math.min(1, Math.max(0, used / cap));
}

/** The human name of a window, for a label. */
export function windowLabel(window: '5h' | '7d' | '30d'): string {
  switch (window) {
    case '5h':
      return '5 hours';
    case '7d':
      return '7 days';
    case '30d':
      return '30 days';
  }
}

/**
 * What a window's fill should look like.
 *
 * Warning at 80% rather than 90%: the 5-hour window is the one that stops a
 * session mid-flow, and being told at 90% leaves about ten minutes of a burst
 * to react in.
 */
export function windowTone(fraction: number | null): 'neutral' | 'warning' | 'danger' {
  if (fraction === null) {
    return 'neutral';
  }
  if (fraction >= 1) {
    return 'danger';
  }
  return fraction >= 0.8 ? 'warning' : 'neutral';
}
